import { useCallback, useEffect, useState } from 'react';
import {
  AddressSearch,
  ApiError,
  Button,
  ErrorNote,
  Input,
  MapLegend,
  RideMap,
  Sheet,
  Spinner,
  api,
  boundsAround,
  boundsOfPoints,
  brl,
  cx,
  haptic,
  km,
  minutes,
  useGeolocation,
  type MapBounds,
} from '@mobi/web';
import { SERVICE_AREA, isInServiceArea, type LatLng } from '@mobi/shared';

interface QuoteOption {
  vehicleType: 'car' | 'moto';
  fareCents: number;
}

interface NearbyDriver {
  id: number;
  lat: number;
  lng: number;
  vehicleType: 'car' | 'moto';
}

/** Carros por perto se atualizam devagar — não é corrida em andamento,
 *  é só "tem gente por aqui" enquanto a pessoa decide se pede. */
const NEARBY_POLL_MS = 6_000;

interface Quote {
  distanceMeters: number;
  durationSeconds: number;
  options: QuoteOption[];
}

/** Janela do mapa quando já se sabe onde a pessoa está. */
const PICK_RADIUS_M = 3_000;

/** Janela quando ainda não se sabe: mostra as duas cidades atendidas. */
const AREA_BOUNDS: MapBounds = SERVICE_AREA.bounds;

const VEHICLE_LABEL = { car: 'Carro', moto: 'Moto' } as const;

export function RequestRide({ onCreated }: { onCreated: () => void }) {
  const geo = useGeolocation(true);

  /**
   * A origem pode vir do GPS ou de um toque no mapa.
   *
   * Depender só do GPS deixava o app inutilizável para quem negou a
   * permissão, está sem sinal, ou usa um aparelho que não localiza. Marcar
   * no mapa sempre funciona.
   */
  const [manualOrigin, setManualOrigin] = useState<LatLng | null>(null);
  const [destination, setDestination] = useState<LatLng | null>(null);
  const [picking, setPicking] = useState<'origin' | 'destination'>('destination');

  const [originAddress, setOriginAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [vehicleType, setVehicleType] = useState<'car' | 'moto'>('car');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const gpsPoint = geo.status === 'ok' ? { lat: geo.coords.lat, lng: geo.coords.lng } : null;
  const origin = manualOrigin ?? gpsPoint;

  /*
   * Com partida e destino definidos, a janela é calculada dos dois
   * pontos — não deixada para o ajuste automático por `markers`. Esse
   * caminho reagiria a qualquer marcador na tela, e um carro por perto
   * aparecendo ou sumindo abriria o zoom sem ter nada a ver com a
   * viagem que está sendo montada.
   */
  const bounds: MapBounds | undefined =
    origin && destination
      ? (boundsOfPoints([origin, destination]) ?? undefined)
      : origin
        ? boundsAround(origin, PICK_RADIUS_M)
        : AREA_BOUNDS;

  const outsideArea =
    (origin && !isInServiceArea(origin)) || (destination && !isInServiceArea(destination));

  const [nearbyDrivers, setNearbyDrivers] = useState<NearbyDriver[]>([]);
  // Arredondado a ~110m: sem isto, cada leitura de GPS (a cada poucos
  // segundos) reiniciaria o polling do zero e nunca completaria um ciclo.
  const nearbyOriginKey = origin ? `${origin.lat.toFixed(3)},${origin.lng.toFixed(3)}` : null;

  useEffect(() => {
    if (!origin) {
      setNearbyDrivers([]);
      return;
    }
    let cancelled = false;
    const poll = () => {
      void api<{ drivers: NearbyDriver[] }>(
        `/api/rides/nearby-drivers?lat=${origin.lat}&lng=${origin.lng}`,
      )
        .then((res) => {
          if (!cancelled) setNearbyDrivers(res.drivers);
        })
        .catch(() => {
          // Falha de rede não pode apagar os pontos que já estavam na
          // tela — some no próximo ciclo bem-sucedido, se sumiram mesmo.
        });
    };
    poll();
    const timer = setInterval(poll, NEARBY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearbyOriginKey]);

  const fetchQuote = useCallback(async (from: LatLng, to: LatLng) => {
    setQuoting(true);
    setError(null);
    try {
      setQuote(await api<Quote>('/api/rides/quote', { body: { origin: from, destination: to } }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível calcular o preço');
      setQuote(null);
    } finally {
      setQuoting(false);
    }
  }, []);

  useEffect(() => {
    if (!origin || !destination) {
      setQuote(null);
      return;
    }
    // Espera o dedo parar antes de cotar: arrastar o destino pela tela não
    // pode disparar uma requisição por pixel.
    const timer = setTimeout(() => void fetchQuote(origin, destination), 400);
    return () => clearTimeout(timer);
    // A origem por GPS muda a cada leitura; recotar por isso seria ruído.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, manualOrigin, gpsPoint === null, fetchQuote]);

  function handlePick(point: LatLng) {
    haptic();
    if (picking === 'origin') {
      setManualOrigin(point);
      // Marcou a partida: o próximo toque naturalmente é o destino.
      setPicking('destination');
    } else {
      setDestination(point);
    }
  }

  async function request() {
    if (!origin || !destination) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/rides', {
        body: {
          vehicleType,
          origin,
          originAddress: originAddress.trim() || 'Partida marcada no mapa',
          destination,
          destinationAddress: destinationAddress.trim() || 'Destino marcado no mapa',
        },
      });
      haptic([10, 40, 10]);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível pedir a corrida');
    } finally {
      setBusy(false);
    }
  }

  const price = quote?.options.find((o) => o.vehicleType === vehicleType)?.fareCents ?? null;

  return (
    <div className="flex h-full flex-col">
      <div className="relative min-h-0 flex-1">
        <RideMap
          className="absolute inset-0"
          bounds={bounds}
          onPick={handlePick}
          markers={[
            ...(origin ? [{ point: origin, kind: 'origin' as const }] : []),
            ...(destination ? [{ point: destination, kind: 'destination' as const }] : []),
            // Sem foto e sem nome: ninguém foi vinculado a ninguém ainda,
            // é só "tem carro por perto" — o mesmo tipo de informação que
            // já era pública, sem identificar quem é quem.
            ...nearbyDrivers.map((d) => ({
              point: { lat: d.lat, lng: d.lng },
              kind: 'driver' as const,
              vehicleType: d.vehicleType,
              id: d.id,
            })),
          ]}
        />

        <div className="pt-safe pointer-events-none absolute inset-x-0 top-0 px-4">
          <div className="elevated-lg pointer-events-auto rounded-3xl border border-line/60 bg-surface/85 px-4 py-3 backdrop-blur-xl">
            {nearbyDrivers.length > 0 && (
              <div className="mb-2.5 flex items-center gap-1.5 border-b border-line/60 pb-2.5 text-xs font-semibold text-brand">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
                </span>
                {nearbyDrivers.length} motorista{nearbyDrivers.length > 1 ? 's' : ''} por perto
              </div>
            )}
            <p className="text-sm font-medium">
              {picking === 'origin'
                ? 'Toque no mapa para marcar de onde você sai'
                : destination
                  ? 'Toque no mapa para mudar o destino'
                  : 'Toque no mapa para marcar o destino'}
            </p>

            {geo.status === 'locating' && (
              <p className="mt-1 flex items-center gap-2 text-sm text-muted">
                <Spinner className="h-4 w-4" /> Procurando sua localização…
              </p>
            )}

            {(geo.status === 'denied' || geo.status === 'unavailable') && !manualOrigin && (
              <div className="mt-2">
                <p className="text-sm text-muted">
                  {geo.status === 'denied'
                    ? 'Localização bloqueada neste navegador.'
                    : 'Não consegui usar o GPS.'}{' '}
                  Marque no mapa de onde você sai.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" onClick={() => geo.retry()}>
                    Tentar GPS de novo
                  </Button>
                  <Button variant="outline" onClick={() => setPicking('origin')}>
                    Marcar partida
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-2 flex items-center justify-between gap-3">
              <MapLegend
                items={[
                  { kind: 'origin', label: origin ? 'Partida' : 'Partida (não marcada)' },
                  { kind: 'destination', label: 'Destino' },
                ]}
              />
              {origin && (
                <button
                  type="button"
                  onClick={() => setPicking(picking === 'origin' ? 'destination' : 'origin')}
                  className="shrink-0 text-xs font-semibold text-brand underline underline-offset-4"
                >
                  {picking === 'origin' ? 'marcar destino' : 'mudar partida'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Sheet className="max-h-[62%] overflow-y-auto">
        <h2 className="text-xl font-bold">Para onde vamos?</h2>

        {/* Digitar e tocar no mapa continuam valendo os dois: quem sabe o
            nome do lugar escreve, quem não sabe aponta. */}
        <div className="mt-4 space-y-3">
          <AddressSearch
            label="Partida"
            placeholder={
              gpsPoint && !manualOrigin ? 'Minha localização' : 'Digite ou toque no mapa'
            }
            value={originAddress || null}
            near={gpsPoint}
            onPick={(place) => {
              setManualOrigin({ lat: place.lat, lng: place.lng });
              setOriginAddress(place.name);
            }}
            onClear={() => setOriginAddress('')}
          />
          <AddressSearch
            label="Destino"
            placeholder="Nome do lugar ou endereço"
            value={destinationAddress || null}
            // A origem, não o GPS bruto: se a pessoa já marcou de onde
            // sai, é dali que o destino faz sentido desempatar — não de
            // onde o celular está fisicamente agora.
            near={origin}
            onPick={(place) => {
              setDestination({ lat: place.lat, lng: place.lng });
              setDestinationAddress(place.name);
            }}
            onClear={() => setDestinationAddress('')}
          />
        </div>

        {outsideArea && (
          <p className="mt-4 rounded-2xl bg-warn/10 px-4 py-3 text-sm text-warn">
            {/* Sem citar cidade por nome: a área atendida é um recorte —
                hoje cobre Aguanil, Campo Belo, Cristais e arredores —,
                e prometer duas cidades específicas ficava errado assim
                que o recorte crescesse sem essa frase acompanhar. */}
            Esse ponto fica fora da nossa área de cobertura. Sem rota calculada, o preço aqui é
            só uma estimativa.
          </p>
        )}

        {origin && destination && (
          <div className="mt-5 grid grid-cols-2 gap-3">
            {(['car', 'moto'] as const).map((type) => {
              const option = quote?.options.find((o) => o.vehicleType === type);
              const selected = vehicleType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => {
                    setVehicleType(type);
                    haptic();
                  }}
                  className={cx(
                    'rounded-2xl border p-4 text-left transition',
                    selected ? 'border-brand bg-brand/10' : 'border-line bg-surface-2',
                  )}
                >
                  <span className="block text-sm font-medium text-muted">
                    {VEHICLE_LABEL[type]}
                  </span>
                  <span className="tabular mt-1 block text-lg font-bold">
                    {quoting ? '…' : brl(option?.fareCents)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {quote && (
          <p className="tabular mt-4 text-sm text-muted">
            {km(quote.distanceMeters)} · {minutes(quote.durationSeconds)} de viagem
          </p>
        )}

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-5">
          <Button
            size="lg"
            full
            loading={busy}
            disabled={!origin || !destination || price === null}
            onClick={request}
          >
            {!origin
              ? 'Marque de onde você sai'
              : !destination
                ? 'Marque o destino'
                : price === null
                  ? 'Calculando…'
                  : `Pedir por ${brl(price)}`}
          </Button>
        </div>

        <p className="mt-3 text-center text-xs text-faint">
          Você paga direto ao motorista, em dinheiro ou na maquininha dele. Seu saldo, se houver, é
          abatido automaticamente.
        </p>
      </Sheet>
    </div>
  );
}
